import { useEffect, useState, useContext, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Filter, Download, Upload, Archive, CheckSquare, FileText, Link2, Trash2, Mail, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { AuthContext, GroupContext } from '@/App';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { parseToISO, isDateOverdue, isDateToday, getDaysFromDue, getTodayISO } from '../utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get display name with organization/group suffix
const getDisplayName = (user, activeGroupId) => {
  if (!user) return '';
  const name = user.name || user.username || 'Unknown';
  
  // Use display_group if available (from /users/with-groups endpoint)
  if (user.display_group) {
    return `${name} (${user.display_group})`;
  }
  
  // Fallback to organization field
  if (user.role === 'external_org' && user.organization) {
    return `${name} (${user.organization})`;
  } else if (user.organization && user.organization !== 'HLRA') {
    return `${name} (${user.organization})`;
  } else {
    // Default - will be updated when using /users/with-groups
    return name;
  }
};

// Helper function to format user display with organization tag (legacy)
const formatUserDisplay = (user) => {
  if (!user) return 'Unassigned';
  return getDisplayName(user) || 'Unassigned';
};

// Get organization badge color
const getOrgBadgeColor = (user) => {
  if (!user) return '';
  
  // Use display_group_color if available
  if (user.display_group_color) {
    return ''; // Will use inline style instead
  }
  
  if (user.role === 'external_org' || (user.organization && user.organization !== 'HLRA')) {
    return 'text-purple-600';
  }
  return 'text-blue-600';
};

export default function Actions() {
  const { user: currentUser } = useContext(AuthContext);
  const { activeGroup } = useContext(GroupContext);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedActions, setSelectedActions] = useState([]);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showDeleteSelectedDialog, setShowDeleteSelectedDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: 'priority', direction: 'asc' });
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assignee: '',
    search: ''
  });

  // Filter users to only show members of the active group
  const groupUsers = useMemo(() => {
    // If no active group context or no users, return all users
    if (!activeGroup?.id || !users.length) return users;
    
    return users.filter(user => {
      // Super admins and platform admins can always be assigned
      if (user.role === 'super_admin' || user.is_platform_admin) return true;
      
      // Check if user has membership in the active group
      const memberships = user.group_memberships || [];
      return memberships.some(m => m.group_id === activeGroup.id);
    });
  }, [users, activeGroup]);

  // Check if current user can export selected actions (Admin/SuperAdmin only)
  const canExportSelected = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';
  
  // Check if current user can create actions (Admin, SuperAdmin, Committee - NOT external_org)
  const canCreateActions = currentUser?.role !== 'external_org';

  const [newAction, setNewAction] = useState({
    title: '',
    description: '',
    owner_id: '',
    date_raised: new Date().toISOString().split('T')[0],
    due_date: '',
    priority: 'Medium',
    category: '',
    status: 'Not Started'
  });

  useEffect(() => {
    fetchActions();
    fetchUsers();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Apply filters locally for assignee and category (backend handles status/priority)
  // Priority order for sorting (Urgent highest priority)
  const priorityOrder = { 'urgent': 0, 'high': 1, 'medium': 2, 'low': 3 };
  
  // Status order for sorting - open statuses first, then closed
  const statusOrder = {
    'in progress': 0,
    'not started': 1,
    'deferred': 2,
    'completed': 3,
    'duplicate': 4,
    'not required': 5
  };
  
  // Helper to check if action is open (not closed)
  const isOpen = (status) => {
    const closedStatuses = ['completed', 'duplicate', 'not required'];
    return !closedStatuses.includes(status?.toLowerCase());
  };
  
  // Sort and filter actions
  const filteredActions = actions
    .filter(action => {
      // Assignee filter
      if (filters.assignee === 'unassigned') {
        // Filter for actions with no assignee
        if (action.owner_id && action.owner_id.trim() !== '') return false;
      } else if (filters.assignee && filters.assignee.trim() !== '' && action.owner_id !== filters.assignee) {
        return false;
      }
      
      // Category filter
      if (filters.category && filters.category.trim() !== '' && action.category !== filters.category) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      switch (sortConfig.field) {
        case 'priority': {
          // First: Open actions before closed actions
          const aIsOpen = isOpen(a.status);
          const bIsOpen = isOpen(b.status);
          if (aIsOpen !== bIsOpen) return aIsOpen ? -1 : 1;
          
          // Second: Sort by priority
          const priorityA = priorityOrder[a.priority?.toLowerCase()] ?? 4;
          const priorityB = priorityOrder[b.priority?.toLowerCase()] ?? 4;
          if (priorityA !== priorityB) return (priorityA - priorityB) * direction;
          
          // Third: Sort by due date using robust parsing
          const dateA = parseToISO(a.due_date);
          const dateB = parseToISO(b.due_date);
          if (dateA && dateB) return dateA.localeCompare(dateB);
          if (dateA) return -1;
          if (dateB) return 1;
          return 0;
        }
        case 'due_date': {
          // Use robust date parsing for sorting
          const dateA = parseToISO(a.due_date);
          const dateB = parseToISO(b.due_date);
          if (dateA && dateB) return dateA.localeCompare(dateB) * direction;
          if (dateA) return -1 * direction;
          if (dateB) return 1 * direction;
          return 0;
        }
        case 'reference': {
          // Sort by action reference number (ACT-0001, ACT-0002, etc.)
          const numA = parseInt(a.reference?.replace(/\D/g, '') || '0');
          const numB = parseInt(b.reference?.replace(/\D/g, '') || '0');
          return (numA - numB) * direction;
        }
        case 'created_at': {
          const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
          const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
          return (dateA - dateB) * direction;
        }
        case 'title': {
          return (a.title || '').localeCompare(b.title || '') * direction;
        }
        case 'status': {
          const statusA = statusOrder[a.status?.toLowerCase()] ?? 6;
          const statusB = statusOrder[b.status?.toLowerCase()] ?? 6;
          return (statusA - statusB) * direction;
        }
        default:
          return 0;
      }
    });

  useEffect(() => {
    fetchActions();
  }, [filters]);

  const fetchActions = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/actions?${params.toString()}`);
      setActions(response.data);
    } catch (error) {
      toast.error('Failed to fetch actions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Use /users/with-groups to get users with their group display info
      const response = await axios.get(`${API}/users/with-groups`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateAction = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!newAction.due_date) {
      toast.error('Due date is required');
      return;
    }
    
    try {
      await axios.post(`${API}/actions`, newAction);
      toast.success('Action created successfully');
      setShowCreateDialog(false);
      setNewAction({
        title: '',
        description: '',
        owner_id: '',
        date_raised: new Date().toISOString().split('T')[0],
        due_date: '',
        priority: 'Medium',
        category: '',
        status: 'Not Started'
      });
      fetchActions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create action');
    }
  };

  const handleSelectAction = (actionId) => {
    setSelectedActions(prev => 
      prev.includes(actionId) 
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedActions.length === filteredActions.length) {
      setSelectedActions([]);
    } else {
      setSelectedActions(filteredActions.map(a => a.id));
    }
  };

  const handleArchiveSelected = async () => {
    try {
      for (const actionId of selectedActions) {
        await axios.delete(`${API}/actions/${actionId}`);
      }
      toast.success(`${selectedActions.length} action(s) archived successfully`);
      setSelectedActions([]);
      setShowArchiveDialog(false);
      fetchActions();
    } catch (error) {
      toast.error('Failed to archive actions: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleArchiveSingle = async (actionId, reference) => {
    if (!window.confirm(`Archive action ${reference}? It can be restored from Admin Tools.`)) return;
    try {
      await axios.delete(`${API}/actions/${actionId}`);
      toast.success(`Action ${reference} archived`);
      fetchActions();
    } catch (error) {
      toast.error('Failed to archive action');
    }
  };

  const handleDeleteSelected = async () => {
    setDeleteLoading(true);
    try {
      // Delete each selected action permanently
      for (const actionId of selectedActions) {
        await axios.delete(`${API}/admin/actions/${actionId}/permanent-with-cleanup`);
      }
      toast.success(`${selectedActions.length} action(s) permanently deleted`);
      setSelectedActions([]);
      setShowDeleteSelectedDialog(false);
      fetchActions();
    } catch (error) {
      toast.error('Failed to delete actions: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleBulkImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: 'binary', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(worksheet, { raw: false, dateNF: 'yyyy-mm-dd' });

        if (data.length === 0) {
          toast.error('No data found in file');
          return;
        }

        // Map user names to IDs
        const userNameToId = {};
        users.forEach(u => {
          userNameToId[u.name?.toLowerCase()] = u.id;
          if (u.username) userNameToId[u.username?.toLowerCase()] = u.id;
        });

        // Map category names to IDs
        const categoryNameToId = {};
        categories.forEach(c => {
          categoryNameToId[c.name?.toLowerCase()] = c.id;
        });

        // Helper to convert Excel date to string
        const formatDate = (value) => {
          if (!value) return '';
          // If it's already a string in date format, return it
          if (typeof value === 'string') {
            // Check if it looks like a date
            if (value.match(/^\d{4}-\d{2}-\d{2}/)) return value.substring(0, 10);
            if (value.match(/^\d{2}\/\d{2}\/\d{4}/)) {
              const [day, month, year] = value.split('/');
              return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
            return value;
          }
          // If it's a number (Excel serial date), convert it
          if (typeof value === 'number') {
            const date = new Date((value - 25569) * 86400 * 1000);
            return date.toISOString().split('T')[0];
          }
          // If it's a Date object
          if (value instanceof Date) {
            return value.toISOString().split('T')[0];
          }
          return String(value);
        };

        // Filter out header rows (rows where Title contains header-like text)
        const headerKeywords = ['title', 'action', 'description', 'assignee', 'reference', 'priority', 'status', 'category', 'due date', 'date raised'];
        const filteredData = data.filter(row => {
          const title = String(row.title || row.Title || row.Action || '').toLowerCase();
          const ref = String(row.reference || row.Reference || row.Ref || '').toLowerCase();
          // Skip if title or reference looks like a header
          const isHeader = headerKeywords.some(kw => title === kw || ref === kw);
          return !isHeader;
        });

        if (filteredData.length === 0) {
          toast.error('No valid data rows found (only headers detected)');
          return;
        }

        const actions = filteredData.map(row => {
          // Try to match assignee by name or ID
          let ownerId = row.owner_id || row.Owner_ID || '';
          const assigneeName = row.assignee || row.Assignee || row.owner || row.Owner || '';
          if (assigneeName && !ownerId) {
            ownerId = userNameToId[assigneeName.toLowerCase()] || '';
          }

          // Try to match category by name or ID
          let categoryId = row.category_id || row.Category_ID || '';
          const categoryName = row.category || row.Category || '';
          if (categoryName && !categoryId) {
            categoryId = categoryNameToId[categoryName.toLowerCase()] || '';
          }

          return {
            reference: String(row.reference || row.Reference || row.Ref || ''),
            title: String(row.title || row.Title || row.Action || ''),
            description: String(row.description || row.Description || row.Details || ''),
            owner_id: ownerId,
            date_raised: formatDate(row.date_raised || row.Date_Raised || row['Date Raised']),
            due_date: formatDate(row.due_date || row.Due_Date || row['Due Date']),
            priority: String(row.priority || row.Priority || 'Medium'),
            category: categoryId,
            status: String(row.status || row.Status || 'Not Started')
          };
        });

        // Validate - show warning for unmatched assignees/categories
        const unmatchedAssignees = actions.filter(a => !a.owner_id).length;
        const unmatchedCategories = actions.filter(a => !a.category).length;

        await axios.post(`${API}/actions/bulk`, actions);
        
        let message = `${actions.length} actions imported successfully`;
        if (unmatchedAssignees > 0 || unmatchedCategories > 0) {
          message += `. Warning: ${unmatchedAssignees} without assignee, ${unmatchedCategories} without category`;
        }
        toast.success(message);
        fetchActions();
      } catch (error) {
        toast.error('Failed to import actions: ' + (error.response?.data?.detail || error.message));
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = ''; // Reset input
  };

  const downloadImportTemplate = () => {
    // Create template with example data and instructions
    const templateData = [
      {
        Reference: 'ACT-0001',
        Title: 'Example action title',
        Description: 'Detailed description of the action',
        Assignee: 'Enter user name exactly as shown in Users list',
        Category: 'Enter category name exactly as shown in Categories list',
        Priority: 'Low / Medium / High / Urgent',
        Status: 'Not Started / In Progress / Completed / Deferred',
        'Due Date': '31/03/2026',
        'Date Raised': '15/01/2026'
      }
    ];
    
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Actions');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 12 }, // Reference
      { wch: 30 }, // Title
      { wch: 40 }, // Description
      { wch: 25 }, // Assignee
      { wch: 20 }, // Category
      { wch: 10 }, // Priority
      { wch: 15 }, // Status
      { wch: 12 }, // Due Date
      { wch: 12 }, // Date Raised
    ];
    
    XLSX.writeFile(wb, 'HLRA_Actions_Import_Template.xlsx');
    toast.success('Template downloaded');
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/reports/export/csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'actions.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Actions exported successfully');
    } catch (error) {
      toast.error('Failed to export actions');
    }
  };

  const handleExportPDF = async () => {
    try {
      // If actions are selected and user is admin/super_admin, export only selected
      if (selectedActions.length > 0 && canExportSelected) {
        toast.info(`Generating PDF for ${selectedActions.length} selected action(s)...`);
        const response = await axios.post(`${API}/reports/export/pdf/selected`, {
          action_ids: selectedActions
        }, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `HLRA_Selected_Actions_${new Date().toISOString().split('T')[0]}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success(`PDF exported with ${selectedActions.length} selected action(s)`);
        return;
      }

      // Otherwise export all filtered actions (original behavior)
      toast.info('Generating PDF...');
      const params = new URLSearchParams();
      if (filters.assignee && filters.assignee.trim()) params.append('assignee', filters.assignee);
      if (filters.status && filters.status.trim()) params.append('status', filters.status);
      if (filters.priority && filters.priority.trim()) params.append('priority', filters.priority);
      if (filters.category && filters.category.trim()) params.append('category', filters.category);
      
      const response = await axios.get(`${API}/reports/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Build filename based on active filters
      let filenameParts = ['HLRA_Actions'];
      if (filters.category && filters.category.trim()) {
        const categoryName = categories.find(c => c.id === filters.category)?.name || 'category';
        filenameParts.push(categoryName.replace(/\s+/g, '_'));
      }
      if (filters.assignee && filters.assignee.trim()) {
        const assigneeName = users.find(u => u.id === filters.assignee)?.name || 'assignee';
        filenameParts.push(assigneeName.replace(/\s+/g, '_'));
      }
      if (filters.status && filters.status.trim()) {
        filenameParts.push(filters.status.replace(/\s+/g, '_'));
      }
      
      link.setAttribute('download', `${filenameParts.join('_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleEmailPDF = async () => {
    try {
      toast.info('Sending report to your email...');
      
      const reportType = selectedActions.length > 0 && canExportSelected ? 'selected_actions' : 'actions';
      const payload = {
        report_type: reportType,
        filters: {
          assignee: filters.assignee || null,
          status: filters.status || null,
          priority: filters.priority || null,
          category: filters.category || null
        },
        action_ids: selectedActions.length > 0 && canExportSelected ? selectedActions : null
      };
      
      const response = await axios.post(`${API}/reports/email-to-me`, payload);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="actions-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Actions</h1>
          <p className="text-gray-600">Manage and track action items</p>
        </div>
        <div className="flex space-x-3">
          {selectedActions.length > 0 && (
            <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50" data-testid="bulk-archive-button">
                  <Archive className="w-4 h-4 mr-2" />
                  Archive ({selectedActions.length})
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Archive {selectedActions.length} Action(s)</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to archive {selectedActions.length} action(s)? 
                    They can be restored later from Admin Tools.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>Cancel</Button>
                  <Button 
                    className="bg-orange-500 hover:bg-orange-600"
                    onClick={handleArchiveSelected}
                  >
                    Archive Actions
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {selectedActions.length > 0 && (
            <Dialog open={showDeleteSelectedDialog} onOpenChange={setShowDeleteSelectedDialog}>
              <DialogTrigger asChild>
                <Button variant="destructive" data-testid="bulk-delete-button">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedActions.length})
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="text-red-600">⚠️ Permanently Delete {selectedActions.length} Action(s)</DialogTitle>
                  <DialogDescription>
                    This will <strong>permanently delete</strong> {selectedActions.length} action(s) including all notes and audit logs.
                    <br /><br />
                    <strong>Selected actions:</strong>
                    <div className="mt-2 max-h-32 overflow-y-auto text-sm">
                      {selectedActions.map(id => {
                        const action = actions.find(a => a.id === id);
                        return action ? (
                          <div key={id} className="py-1">{action.reference}: {action.title?.substring(0, 40)}...</div>
                        ) : null;
                      })}
                    </div>
                    <br />
                    <span className="text-red-600">This cannot be undone!</span>
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeleteSelectedDialog(false)}>Cancel</Button>
                  <Button 
                    variant="destructive"
                    onClick={handleDeleteSelected}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Deleting...' : 'Delete Permanently'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <input
            type="file"
            id="bulk-import"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleBulkImport}
          />
          <div className="flex">
            <Button
              variant="outline"
              onClick={() => document.getElementById('bulk-import').click()}
              data-testid="bulk-import-button"
              className="rounded-r-none border-r-0"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button
              variant="outline"
              onClick={downloadImportTemplate}
              data-testid="download-template-button"
              className="rounded-l-none px-2"
              title="Download import template"
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>
          <Button variant="outline" onClick={handleExport} data-testid="export-button">
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                data-testid="export-pdf-button"
                className={selectedActions.length > 0 && canExportSelected 
                  ? "text-white bg-red-600 hover:bg-red-700 border-red-600" 
                  : "text-red-600 border-red-300 hover:bg-red-50"}
              >
                <FileText className="w-4 h-4 mr-2" />
                {selectedActions.length > 0 && canExportSelected 
                  ? `PDF (${selectedActions.length})` 
                  : "PDF"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportPDF}>
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleEmailPDF}>
                <Mail className="w-4 h-4 mr-2" />
                Email to me
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canCreateActions && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-green-500 hover:from-blue-600 hover:to-green-600" data-testid="create-action-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Action
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Action</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateAction} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={newAction.title}
                    onChange={(e) => setNewAction({...newAction, title: e.target.value})}
                    required
                    data-testid="title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={newAction.description}
                    onChange={(e) => setNewAction({...newAction, description: e.target.value})}
                    rows={4}
                    required
                    data-testid="description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={newAction.category}
                      onValueChange={(value) => setNewAction({...newAction, category: value})}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="owner">Assign To *</Label>
                    <Select
                      value={newAction.owner_id}
                      onValueChange={(value) => setNewAction({...newAction, owner_id: value})}
                    >
                      <SelectTrigger data-testid="owner-select">
                        <SelectValue placeholder="Select owner" />
                      </SelectTrigger>
                      <SelectContent>
                        {groupUsers.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            <span className="flex items-center gap-2">
                              <span 
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: user.display_group_color || '#3B82F6' }}
                              />
                              {user.name}
                              <span 
                                className="text-xs px-1.5 py-0.5 rounded-full"
                                style={{ 
                                  backgroundColor: user.display_group_color ? `${user.display_group_color}20` : '#3B82F620',
                                  color: user.display_group_color || '#3B82F6'
                                }}
                              >
                                {user.display_group || (activeGroup?.name || 'HLRA')}
                              </span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_raised">Date Raised *</Label>
                    <Input
                      id="date_raised"
                      type="date"
                      value={newAction.date_raised}
                      onChange={(e) => setNewAction({...newAction, date_raised: e.target.value})}
                      required
                      data-testid="date-raised-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="due_date">Due Date *</Label>
                    <Input
                      id="due_date"
                      type="date"
                      value={newAction.due_date}
                      onChange={(e) => setNewAction({...newAction, due_date: e.target.value})}
                      data-testid="due-date-input"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select
                      value={newAction.priority}
                      onValueChange={(value) => setNewAction({...newAction, priority: value})}
                    >
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={newAction.status}
                      onValueChange={(value) => setNewAction({...newAction, status: value})}
                    >
                      <SelectTrigger data-testid="status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Not Started">Not Started</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Completed">Completed</SelectItem>
                        <SelectItem value="Deferred">Deferred</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-blue-500 to-green-500" data-testid="submit-action-button">
                    Create Action
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search actions..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All Statuses</SelectItem>
                <SelectItem value="Not Started">Not Started</SelectItem>
                <SelectItem value="In Progress">In Progress</SelectItem>
                <SelectItem value="Completed">Completed</SelectItem>
                <SelectItem value="Deferred">Deferred</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.priority} onValueChange={(value) => setFilters({...filters, priority: value})}>
              <SelectTrigger data-testid="priority-filter">
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All Priorities</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.category} onValueChange={(value) => setFilters({...filters, category: value})}>
              <SelectTrigger data-testid="category-filter">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.assignee} onValueChange={(value) => setFilters({...filters, assignee: value})}>
              <SelectTrigger data-testid="assignee-filter">
                <SelectValue placeholder="Filter by assignee" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All Assignees</SelectItem>
                <SelectItem value="unassigned" className="text-orange-600 font-medium">Unassigned</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    <span className="flex items-center gap-2">
                      <span 
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: user.display_group_color || '#6B7280' }}
                      />
                      {user.name}
                      <span 
                        className="text-xs px-1.5 py-0.5 rounded-full"
                        style={{ 
                          backgroundColor: user.display_group_color ? `${user.display_group_color}20` : '#6B728020',
                          color: user.display_group_color || '#6B7280'
                        }}
                      >
                        {user.display_group || 'Unknown'}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => setFilters({ status: '', priority: '', assignee: '', category: '', search: '' })}
              data-testid="clear-filters-button"
            >
              <Filter className="w-4 h-4 mr-2" />
              Clear Filters
            </Button>
          </div>
          
          {/* Sort Controls */}
          <div className="flex items-center gap-3 mt-4 pt-4 border-t">
            <span className="text-sm text-gray-500 font-medium">Sort by:</span>
            <div className="flex gap-2">
              {[
                { field: 'priority', label: 'Priority' },
                { field: 'due_date', label: 'Due Date' },
                { field: 'action_id', label: 'Action #' },
                { field: 'created_at', label: 'Created' },
                { field: 'title', label: 'Title' },
                { field: 'status', label: 'Status' },
              ].map(({ field, label }) => (
                <Button
                  key={field}
                  variant={sortConfig.field === field ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (sortConfig.field === field) {
                      // Toggle direction if same field
                      setSortConfig({ field, direction: sortConfig.direction === 'asc' ? 'desc' : 'asc' });
                    } else {
                      // New field, default to ascending
                      setSortConfig({ field, direction: 'asc' });
                    }
                  }}
                  className={sortConfig.field === field ? "bg-blue-600 hover:bg-blue-700" : ""}
                  data-testid={`sort-${field}`}
                >
                  {label}
                  {sortConfig.field === field && (
                    sortConfig.direction === 'asc' 
                      ? <ArrowUp className="w-3 h-3 ml-1" />
                      : <ArrowDown className="w-3 h-3 ml-1" />
                  )}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Actions ({filteredActions.length})</CardTitle>
            {filteredActions.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={handleSelectAll}
                className="text-gray-600"
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                {selectedActions.length === filteredActions.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : filteredActions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No actions found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredActions.map((action) => {
                const owner = users.find(u => u.id === action.owner_id);
                const category = categories.find(c => c.id === action.category);
                const isSelected = selectedActions.includes(action.id);
                return (
                  <div
                    key={action.id}
                    className={`flex items-start p-4 border-2 rounded-lg hover:bg-gray-50 transition card-hover ${isSelected ? 'bg-orange-50 border-orange-400' : 'border-gray-200'}`}
                    data-testid={`action-${action.id}`}
                  >
                    <div className="flex items-center mr-4 pt-1">
                      <div 
                        onClick={() => handleSelectAction(action.id)}
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                          isSelected 
                            ? 'bg-orange-500 border-orange-500 text-white' 
                            : 'border-gray-300 hover:border-orange-400 bg-white'
                        }`}
                      >
                        {isSelected && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <Link
                      to={`/actions/${action.id}`}
                      className="flex-1"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {(() => {
                            // Use imported date utilities for robust parsing
                            const closedStatuses = ['Completed', 'Duplicate', 'Not Required'];
                            const isDueTodayCheck = isDateToday(action.due_date) && !closedStatuses.includes(action.status);
                            const isOverdueCheck = isDateOverdue(action.due_date) && !closedStatuses.includes(action.status);
                            const daysOverdue = isOverdueCheck ? getDaysFromDue(action.due_date) : 0;
                            
                            return (
                              <>
                                <div className="flex items-center space-x-3 mb-2">
                                  <span className={`text-xs font-mono px-2 py-1 rounded ${isOverdueCheck ? 'bg-red-100 text-red-700 font-bold' : isDueTodayCheck ? 'bg-orange-100 text-orange-700 font-bold' : 'text-gray-500 bg-gray-100'}`}>
                                    {action.reference}
                                  </span>
                                  {isOverdueCheck && (
                                    <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-medium animate-pulse">
                                      {daysOverdue}d OVERDUE
                                    </span>
                                  )}
                                  {isDueTodayCheck && (
                                    <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full font-medium animate-pulse">
                                      DUE TODAY
                                    </span>
                                  )}
                                  {action.linked_actions && action.linked_actions.length > 0 && (
                                    <span className="text-xs text-blue-600 flex items-center gap-1" title={`${action.linked_actions.length} linked action(s)`}>
                                      <Link2 className="w-3 h-3" />
                                      {action.linked_actions.length}
                                    </span>
                                  )}
                                  <span className={`status-badge priority-${action.priority.toLowerCase()}`}>
                                    {action.priority}
                                  </span>
                                  <span className={`status-badge status-${action.status.toLowerCase().replace(' ', '-')}`}>
                                    {action.status}
                                  </span>
                                </div>
                                <h3 className={`text-lg font-semibold mb-1 ${isOverdueCheck ? 'text-red-700 font-bold' : isDueTodayCheck ? 'text-orange-700 font-semibold' : 'text-gray-900'}`}>{action.title}</h3>
                                <p className="text-sm text-gray-600 line-clamp-2 mb-2">{action.description}</p>
                                <div className="flex items-center space-x-4 text-xs text-gray-500">
                                  <span>Assigned to: <span className={`font-medium ${getOrgBadgeColor(owner)}`}>{formatUserDisplay(owner)}</span></span>
                                  {action.due_date && (
                                    <span className={isOverdueCheck ? 'text-red-600 font-medium' : ''}>
                                      Due: <span className={`font-medium ${isOverdueCheck ? 'text-red-700' : 'text-gray-700'}`}>{new Date(action.due_date).toLocaleDateString('en-GB')}</span>
                                    </span>
                                  )}
                                  <span>Category: <span className="font-medium text-gray-700">{category?.name || 'N/A'}</span></span>
                                  {action.status_changed_date && (
                                    <span>Status Changed: <span className="font-medium text-gray-700">{new Date(action.status_changed_date).toLocaleDateString('en-GB')}</span></span>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault();
                        handleArchiveSingle(action.id, action.reference);
                      }}
                      className="ml-2 text-orange-600 hover:bg-orange-50"
                      title="Archive this action"
                    >
                      <Archive className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}